// Types
import { DeviceFormFactor, MilanoteApplicationMode } from '../../../common/platform/platformTypes';

// Constants
import { GRID_BREAKPOINTS } from '../../../common/device/breakpointConstants';

/**
 * Below this many CSS pixels on its shorter side a window is treated as phone-sized.
 * It is the existing mobile grid breakpoint and Android phone/tablet cut-off, and sits close to
 * UIKit's compact/regular boundary, so iPhone landscape (short side under 450) stays on the phone layout.
 */
export const PHONE_WINDOW_MAX_SHORT_SIDE_PX = GRID_BREAKPOINTS.SMALL;

const IOS_CAPACITOR_PLATFORM = 'ios';

// Sizes are compared in CSS pixels; a one pixel difference is rounding, not a resized window.
const WINDOW_SIZE_TOLERANCE_PX = 1;

const isSameSize = (sizeA: number, sizeB: number): boolean => Math.abs(sizeA - sizeB) <= WINDOW_SIZE_TOLERANCE_PX;

/**
 * Only the iOS app derives its form factor from the window.
 * iPad Stage Manager windows and a folding iPhone change size mid-session while the device,
 * and therefore its user agent, stay the same.
 */
export const isFormFactorWindowDerived = (capacitorPlatform: string): boolean =>
    capacitorPlatform === IOS_CAPACITOR_PLATFORM;

/**
 * A window is phone-sized when either dimension is small.
 * Judging the shorter side keeps iPhone landscape on the phone layout and only promotes
 * genuinely tablet-shaped windows: an unfolded phone or a wide Stage Manager window.
 */
export const getWindowFormFactor = (windowWidth: number, windowHeight: number): DeviceFormFactor =>
    Math.min(windowWidth, windowHeight) < PHONE_WINDOW_MAX_SHORT_SIDE_PX
        ? DeviceFormFactor.phone
        : DeviceFormFactor.tablet;

/**
 * Whether the window covers the whole display.
 * `screen.*` is the display, so the two differ in Stage Manager, Split View and any resizable window.
 * Short and long sides are compared rather than width and height, because `screen.*` does not reliably follow rotation.
 */
export const getWindowFillsScreen = (
    windowWidth: number,
    windowHeight: number,
    screenWidth: number,
    screenHeight: number,
): boolean =>
    isSameSize(Math.min(windowWidth, windowHeight), Math.min(screenWidth, screenHeight)) &&
    isSameSize(Math.max(windowWidth, windowHeight), Math.max(screenWidth, screenHeight));

/**
 * On the native apps the application mode follows the form factor.
 * Tablet-sized windows get the desktop workspace and phone-sized windows the mobile one.
 */
export const getNativeAppModeForFormFactor = (formFactor: DeviceFormFactor): MilanoteApplicationMode =>
    formFactor === DeviceFormFactor.tablet ? MilanoteApplicationMode.desktop : MilanoteApplicationMode.mobile;
